import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  flexRender,
} from '@tanstack/react-table'
import axios from 'axios'
import { AuthContext } from '../AuthContext'
import { Container, ProgressBar } from 'react-bootstrap'
import Cookies from 'js-cookie'
import { useNavigate } from 'react-router-dom'
import { Helmet } from 'react-helmet'

// 自定義日期排序函數
const dateStringSorter = (rowA, rowB, columnId) => {
  const dateA = rowA.getValue(columnId)
  const dateB = rowB.getValue(columnId)

  // Check if either date is empty/invalid
  const isValidA = dateA && !isNaN(new Date(dateA).getTime())
  const isValidB = dateB && !isNaN(new Date(dateB).getTime())

  // If both are invalid/empty, maintain their relative order
  if (!isValidA && !isValidB) return 0

  // 空值或非日期放到最後
  if (!isValidA) return 1
  if (!isValidB) return -1

  // If both are valid dates, compare them normally
  return new Date(dateA) - new Date(dateB)
}

// 自定義過濾函數
const filterFns = {
  includesSome: (row, columnId, filterValue) => {
    const rowValues = Array.isArray(row.getValue(columnId))
      ? row.getValue(columnId)
      : [row.getValue(columnId)]
    return filterValue.length === 0
      ? true
      : filterValue.some((value) => rowValues.includes(value))
  },
}

// 搜尋框組件
const SearchBox = ({ column, title, onClose }) => {
  // 添加 ref 用於 focus
  const inputRef = useRef(null)

  // 組件掛載時自動 focus
  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  // 使用 useEffect 添加全局點擊監聽
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.search-container')) {
        onClose()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [onClose])

  return (
    <div
      className="search-container"
      style={{
        position: 'absolute',
        top: '100%',
        left: 0,
        right: 0,
        background: 'white',
        padding: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        zIndex: 1000,
        display: 'flex',
        gap: '8px',
        width: 'fit-content',
        border: '#CCC 1px solid',
        borderRadius: '8px',
      }}
    >
      <input
        ref={inputRef}
        value={column.getFilterValue() || ''}
        onChange={(e) => column.setFilterValue(e.target.value)}
        placeholder={`搜尋${title}...`}
        style={{
          flex: 1,
          padding: '4px',
          border: '1px solid #ddd',
          borderRadius: '4px',
        }}
        onClick={(e) => e.stopPropagation()}
      />
      <button
        onClick={(e) => {
          e.stopPropagation()
          column.setFilterValue('')
          inputRef.current?.focus()
        }}
        style={{
          padding: '4px 8px',
          border: '1px solid #ddd',
          borderRadius: '4px',
          background: 'white',
          cursor: 'pointer',
          color: '#666',
        }}
      >
        <i className="fa-solid fa-rotate-left" />
      </button>
      <button
        onClick={(e) => {
          e.stopPropagation()
          onClose()
        }}
        style={{
          padding: '4px 8px',
          border: '1px solid #ddd',
          borderRadius: '4px',
          background: 'white',
          cursor: 'pointer',
          color: '#666',
        }}
      >
        <i className="fa-solid fa-xmark" />
      </button>
    </div>
  )
}

// 膠囊標籤組件
const StatusPill = ({ status }) => {
  const style = {
    padding: '4px 12px',
    borderRadius: '20px',
    display: 'inline-block',
    fontSize: '14px',
  }

  if (status === '已串接') {
    return (
      <span
        style={{
          ...style,
          backgroundColor: '#e8f5e9',
          color: '#2e7d32',
          border: '1px solid #c8e6c9',
        }}
      >
        {status}
      </span>
    )
  }
  return (
    <span
      style={{
        ...style,
        backgroundColor: '#ffebee',
        color: '#c62828',
        border: '1px solid #ffcdd2',
      }}
    >
      {status}
    </span>
  )
}

// 計劃標籤組件
const ProgramPill = ({ program }) => {
  // 為不同計劃設置不同顏色
  const colors = {
    代謝計畫: { bg: '#f3e5f5', color: '#4a148c', border: '#e1bee7' },
    腎臟: { bg: '#e3f2fd', color: '#0d47a1', border: '#bbdefb' },
    氣喘: { bg: '#e8f5e9', color: '#1b5e20', border: '#c8e6c9' },
    高血壓: { bg: '#fce4ec', color: '#880e4f', border: '#f8bbd0' },
    高血脂: { bg: '#f9fbe7', color: '#827717', border: '#f0f4c3' },
    糖尿病: { bg: '#fff3e0', color: '#e65100', border: '#ffe0b2' },
  }

  const style = colors[program] || {
    bg: '#f5f5f5',
    color: '#212121',
    border: '#e0e0e0',
  }

  return (
    <span
      style={{
        padding: '4px 12px',
        borderRadius: '20px',
        backgroundColor: style.bg,
        color: style.color,
        border: `1px solid ${style.border}`,
        display: 'inline-block',
        fontSize: '14px',
        marginRight: '4px',
        marginBottom: '4px',
      }}
    >
      {program}
    </span>
  )
}

// 搜尋結果螢光筆效果
const HighlightText = ({ text, searchTerm }) => {
  if (!searchTerm) return text

  const parts = text.split(new RegExp(`(${searchTerm})`, 'gi'))
  return (
    <span>
      {parts.map((part, index) =>
        part.toLowerCase() === searchTerm.toLowerCase() ? (
          <span
            key={index}
            style={{
              backgroundColor: '#67c7aa',
              borderRadius: '2px',
              padding: '0 2px',
            }}
          >
            {part}
          </span>
        ) : (
          part
        )
      )}
    </span>
  )
}

// 可搜尋的標題組件
const SearchableHeader = ({
  column,
  title,
  activeSearchColumn,
  setActiveSearchColumn,
}) => {
  const isSearchVisible = activeSearchColumn === column.id
  const hasFilter = column.getFilterValue()

  return (
    <div
      style={{ position: 'relative' }}
      onClick={(e) => {
        e.stopPropagation()
        setActiveSearchColumn(isSearchVisible ? null : column.id)
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: '8px 0',
          cursor: 'pointer',
          justifyContent: 'space-between',
        }}
      >
        <span>{title}</span>
        <i
          className="fa-solid fa-magnifying-glass"
          style={{
            marginLeft: '8px',
            color: hasFilter ? '#67c7aa' : '#BBB',
          }}
        />
      </div>
      {isSearchVisible && (
        <SearchBox
          column={column}
          title={title}
          onClose={() => setActiveSearchColumn(null)}
        />
      )}
    </div>
  )
}

// 排序指示器組件
const SortIndicator = ({ isSorted }) => {
  if (!isSorted) {
    return (
      <i
        className="fa-solid fa-sort"
        style={{ marginLeft: '4px', color: '#BBB' }}
      />
    )
  }
  return isSorted === 'desc' ? (
    <i
      className="fa-solid fa-sort-up"
      style={{ marginLeft: '4px', color: '#67c7aa' }}
    />
  ) : (
    <i
      className="fa-solid fa-sort-down"
      style={{ marginLeft: '4px', color: '#67c7aa' }}
    />
  )
}

// 過濾器彈出視窗基礎組件
const FilterContainer = ({ children, onClose }) => {
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.filter-container')) {
        onClose()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [onClose])

  return (
    <div
      className="filter-container"
      style={{
        position: 'absolute',
        top: '100%',
        left: 0,
        background: 'white',
        padding: '12px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        zIndex: 1000,
        width: 'fit-content',
        border: '#CCC 1px solid',
        borderRadius: '8px',
        minWidth: '150px',
      }}
    >
      {children}
    </div>
  )
}

// App串接過濾器組件
const AppIntegrationFilter = ({ column, onClose }) => {
  const options = ['已串接', '未串接']
  const currentFilter = column.getFilterValue() || []

  return (
    <FilterContainer onClose={onClose}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            marginBottom: '8px',
          }}
        >
          {options.map((option) => (
            <label
              key={option}
              style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
            >
              <input
                type="checkbox"
                checked={currentFilter.includes(option)}
                onChange={() => {
                  if (currentFilter.includes(option)) {
                    column.setFilterValue(
                      currentFilter.filter((item) => item !== option)
                    )
                  } else {
                    column.setFilterValue([...currentFilter, option])
                  }
                }}
              />
              <StatusPill status={option} />
            </label>
          ))}
        </div>
        <div
          style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}
        >
          <button
            onClick={() => {
              column.setFilterValue([])
            }}
            style={{
              padding: '4px 8px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              background: 'white',
              cursor: 'pointer',
              color: '#666',
            }}
          >
            <i className="fa-solid fa-rotate-left" />
          </button>
          <button
            onClick={onClose}
            style={{
              padding: '4px 8px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              background: 'white',
              cursor: 'pointer',
              color: '#666',
            }}
          >
            <i className="fa-solid fa-xmark" />
          </button>
        </div>
      </div>
    </FilterContainer>
  )
}

// 計劃過濾器組件
const ProgramFilter = ({ column, onClose }) => {
  const options = [
    '代謝計畫',
    '腎臟',
    '肝炎',
    '氣喘',
    '自費A',
    '高血壓',
    '高血脂',
    '糖尿病',
  ]
  const currentFilter = column.getFilterValue() || []

  return (
    <FilterContainer onClose={onClose}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            marginBottom: '8px',
          }}
        >
          {options.map((option) => (
            <label
              key={option}
              style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
            >
              <input
                type="checkbox"
                checked={currentFilter.includes(option)}
                onChange={() => {
                  if (currentFilter.includes(option)) {
                    column.setFilterValue(
                      currentFilter.filter((item) => item !== option)
                    )
                  } else {
                    column.setFilterValue([...currentFilter, option])
                  }
                }}
              />
              <ProgramPill program={option} />
            </label>
          ))}
        </div>
        <div
          style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}
        >
          <button
            onClick={() => {
              column.setFilterValue([])
            }}
            style={{
              padding: '4px 8px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              background: 'white',
              cursor: 'pointer',
              color: '#666',
            }}
          >
            <i className="fa-solid fa-rotate-left" />
          </button>
          <button
            onClick={onClose}
            style={{
              padding: '4px 8px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              background: 'white',
              cursor: 'pointer',
              color: '#666',
            }}
          >
            <i className="fa-solid fa-xmark" />
          </button>
        </div>
      </div>
    </FilterContainer>
  )
}

// 為列標題創建單獨的組件
const ProgramHeader = ({ column }) => {
  const [isFilterVisible, setIsFilterVisible] = useState(false)
  const hasFilter = column.getFilterValue()?.length > 0

  return (
    <div style={{ position: 'relative' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
        }}
        onClick={() => setIsFilterVisible(!isFilterVisible)}
      >
        <span>參與計劃</span>
        <i
          className="fa-solid fa-filter"
          style={{
            marginLeft: '8px',
            color: hasFilter ? '#67c7aa' : '#BBB',
          }}
        />
      </div>
      {isFilterVisible && (
        <ProgramFilter
          column={column}
          onClose={() => setIsFilterVisible(false)}
        />
      )}
    </div>
  )
}

const AppIntegrationHeader = ({ column }) => {
  const [isFilterVisible, setIsFilterVisible] = useState(false)
  const hasFilter = column.getFilterValue()?.length > 0

  return (
    <div style={{ position: 'relative' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
        }}
        onClick={() => setIsFilterVisible(!isFilterVisible)}
      >
        <span>App 串接</span>
        <i
          className="fa-solid fa-filter"
          style={{
            marginLeft: '8px',
            color: hasFilter ? '#67c7aa' : '#BBB',
          }}
        />
      </div>
      {isFilterVisible && (
        <AppIntegrationFilter
          column={column}
          onClose={() => setIsFilterVisible(false)}
        />
      )}
    </div>
  )
}

// 資料轉換函數
const transformPatientData = (apiData) => {
  return apiData.map((patient) => ({
    id: patient.patient_id,
    name: patient.name,
    idNumber: patient.id_number,
    phone: patient.phone,
    appIntegration: patient.linked_ha_app === 1 ? '已串接' : '未串接',
    program: patient.project.map((p) => p.project_name),
    lastVisitDate: patient.project[0]?.last_appointment_time || '',
    nextVisitDate: patient.project[0]?.next_appointment_time || '',
  }))
}

const PatientData = () => {
  const navigate = useNavigate()
  const [data, setData] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [activeSearchColumn, setActiveSearchColumn] = useState(null)
  const [currentPage, setCurrentPage] = useState(0)
  // const [globalFilter, setGlobalFilter] = useState('')
  const pageSize = 10
  const { baseUrl, currentUser, extendCookie, setLoginShow } =
    useContext(AuthContext)
  const getAll = '/api/crm/patient/getAll'

  const sessionFromCookie = Cookies.get('session')
  const { session = '' } = sessionFromCookie
    ? JSON.parse(sessionFromCookie)
    : {}

  const handleRowClick = (patientId) => {
    navigate(`/patient/${patientId}`)
  }

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true)

        if (!session) {
          alert('登入已逾時，請重新登入')
          setLoginShow(true)
          throw new Error('Session not found')
        }

        const response = await axios.post(
          baseUrl + getAll,
          {},
          {
            headers: {
              'Content-Type': 'application/json',
              Accept: '*/*',
              session: currentUser?.session || session || '',
            },
          }
        )
        console.log('response.data', response.data)
        if (response.data.data) {
          const transformedData = transformPatientData(response.data.data)
          setData(transformedData)
          setError(null)
          extendCookie()
        } else if (response.data.error.code === '50004') {
          alert('登入已逾時，請重新登入')
          setLoginShow(true)
        } else {
          throw new Error('No data received')
        }
      } catch (err) {
        setError('獲取數據時發生錯誤')
        console.error('Error fetching data:', err)
      } finally {
        setIsLoading(false)
      }
    }

    if (session) {
      fetchData()
    } else {
      setLoginShow(true)
    }
  }, [])

  const columns = useMemo(
    () => [
      {
        id: 'selection',
        header: ({ table }) => (
          <input
            type="checkbox"
            checked={table.getIsAllPageRowsSelected()}
            ref={(input) => {
              if (input) {
                input.indeterminate = table.getIsSomePageRowsSelected()
              }
            }}
            onChange={table.getToggleAllPageRowsSelectedHandler()}
          />
        ),
        cell: ({ row }) => (
          <input
            type="checkbox"
            {...{
              checked: row.getIsSelected(),
              disabled: !row.getCanSelect(),
              onChange: row.getToggleSelectedHandler(),
            }}
          />
        ),
      },
      {
        accessorKey: 'lastVisitDate',
        sortingFn: dateStringSorter,
        enableSorting: true,
        // 自定義標題渲染
        header: ({ column }) => (
          <div
            style={{
              display: 'flex',
              alignItems: 'center', // 水平置中
              height: '100%',
              padding: '8px 0', // 增加上下內間距
              justifyContent: 'space-between',
            }}
          >
            <span>最近來診日期</span>
            <SortIndicator isSorted={column.getIsSorted()} />
          </div>
        ),
      },
      {
        accessorKey: 'name',
        filterFn: 'includesString',
        enableSorting: false,
        header: ({ column }) => (
          <SearchableHeader
            column={column}
            title="姓名"
            activeSearchColumn={activeSearchColumn}
            setActiveSearchColumn={setActiveSearchColumn}
          />
        ),
        cell: ({ getValue, column }) => (
          <HighlightText
            text={getValue()}
            searchTerm={column.getFilterValue() || ''}
          />
        ),
      },
      {
        accessorKey: 'phone',
        filterFn: 'includesString',
        enableSorting: false,
        header: ({ column }) => (
          <SearchableHeader
            column={column}
            title="手機"
            activeSearchColumn={activeSearchColumn}
            setActiveSearchColumn={setActiveSearchColumn}
          />
        ),
        cell: ({ getValue, column }) => (
          <HighlightText
            text={getValue()}
            searchTerm={column.getFilterValue() || ''}
          />
        ),
      },
      {
        accessorKey: 'idNumber',
        filterFn: 'includesString',
        enableSorting: false,
        header: ({ column }) => (
          <SearchableHeader
            column={column}
            title="身份證字號"
            activeSearchColumn={activeSearchColumn}
            setActiveSearchColumn={setActiveSearchColumn}
          />
        ),
        cell: ({ getValue, column }) => (
          <HighlightText
            text={getValue()}
            searchTerm={column.getFilterValue() || ''}
          />
        ),
      },
      {
        accessorKey: 'program',
        filterFn: 'includesSome',
        enableSorting: false,
        header: ({ column }) => <ProgramHeader column={column} />,
        cell: (info) => (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
            {info.getValue().map((program, index) => (
              <ProgramPill key={index} program={program} />
            ))}
          </div>
        ),
      },
      {
        accessorKey: 'nextVisitDate',
        sortingFn: dateStringSorter,
        enableSorting: true,
        // 自定義標題渲染
        header: ({ column }) => (
          <div
            style={{
              display: 'flex',
              alignItems: 'center', // 水平置中
              height: '100%',
              padding: '8px 0', // 增加上下內間距
              justifyContent: 'space-between',
            }}
          >
            <span>下次回診日期</span>
            <SortIndicator isSorted={column.getIsSorted()} />
          </div>
        ),
      },
      {
        accessorKey: 'appIntegration',
        filterFn: 'includesString',
        enableSorting: false,
        header: ({ column }) => <AppIntegrationHeader column={column} />,
        cell: (info) => <StatusPill status={info.getValue()} />,
      },
    ],
    [activeSearchColumn]
  )

  const table = useReactTable({
    data,
    columns,
    filterFns,
    state: {
      // globalFilter,
      pagination: {
        pageSize,
        pageIndex: currentPage,
      },
    },
    // onGlobalFilterChange: setGlobalFilter,
    // globalFilterFn: 'includesString',
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    debugTable: true,
    onPaginationChange: (setPagination) => {
      const newPagination = setPagination((prevState) => ({
        ...prevState,
        pageIndex: currentPage,
        pageSize,
      }))
      if (newPagination.pageIndex !== currentPage) {
        setCurrentPage(newPagination.pageIndex)
      }
    },
  })

  const totalPages = Math.ceil(
    table.getFilteredRowModel().rows.length / pageSize
  )

  if (isLoading) {
    return (
      <Container className="text-center">
        <div className="m-5">
          <ProgressBar className="m-4" animated now={100} />
          <h5 className="text-secondary">資料等待中...</h5>
        </div>
      </Container>
    )
  }

  if (error) {
    return (
      <Container
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: '80vh' }}
      >
        <p className="text-muted">{error}</p>
      </Container>
    )
  }

  return (
    <>
      <Helmet>
        <title>用戶數據 | HA! CRM</title>
      </Helmet>
      <Container
        style={{
          display: 'flex',
          justifyContent: 'center',
          padding: '20px',
          width: '100%',
          marginTop: '1rem',
          textAlign: 'center',
        }}
        className="patientData"
      >
        <div
          style={{ width: '100%', maxWidth: '1200px' }}
          onClick={() => setActiveSearchColumn(null)}
        >
          <div className="title mb-5">
            <h2>用戶數據</h2>
          </div>
          {/* <input
          value={globalFilter || ''}
          onChange={(e) => setGlobalFilter(e.target.value)}
          placeholder="搜尋姓名、手機或身份證字號"
          style={{ marginLeft: '10px', marginBottom: '10px' }}
        /> */}
          <table
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              marginTop: '20px',
            }}
          >
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id} style={{ backgroundColor: '#f5f5f5' }}>
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      style={{
                        borderBottom: '1px solid #e0e0e0',
                        padding: '12px 16px',
                        textAlign: 'left',
                      }}
                    >
                      {header.isPlaceholder ? null : (
                        <div>
                          <div
                            {...{
                              onClick: header.column.getCanSort()
                                ? header.column.getToggleSortingHandler()
                                : undefined,
                              style: {
                                cursor: header.column.getCanSort()
                                  ? 'pointer'
                                  : 'default',
                              },
                            }}
                          >
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                          </div>
                        </div>
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row) => {
                return (
                  <tr
                    key={row.id}
                    style={{
                      borderBottom: '1px solid #e0e0e0',
                      cursor: 'pointer',
                    }}
                    className="table-row"
                    onClick={() => handleRowClick(row.original.id)}
                  >
                    {row.getVisibleCells().map((cell) => {
                      return (
                        <td
                          key={cell.id}
                          style={{
                            padding: '12px 16px',
                          }}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </td>
                      )
                    })}
                  </tr>
                )
              })}
            </tbody>
          </table>

          {/* 分頁控制 */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              marginTop: '40px',
              gap: '8px',
            }}
          >
            <div
              onClick={() => table.previousPage()}
              style={{
                padding: '4px 8px',
                borderRadius: '4px',
                color: !table.getCanPreviousPage() ? '#ccc' : '#666',
                cursor: !table.getCanPreviousPage() ? 'not-allowed' : 'pointer',
              }}
            >
              <i className="fa-solid fa-angle-left" />
            </div>
            {/* Page numbers with ellipsis */}
            {Array.from({ length: totalPages }).map((_, index) => {
              // Show first page, last page, current page, and pages around current
              const shouldShow =
                index === 0 || // First page
                index === totalPages - 1 || // Last page
                Math.abs(index - currentPage) <= 2 // Pages around current

              // Show ellipsis instead of page numbers
              if (!shouldShow && (index === 1 || index === totalPages - 2)) {
                return (
                  <div key={index} style={{ color: '#666' }}>
                    <i className="fa-solid fa-ellipsis" />
                  </div>
                )
              }

              // Hide other page numbers
              if (!shouldShow) {
                return null
              }

              return (
                <div
                  key={index}
                  onClick={() => setCurrentPage(index)}
                  style={{
                    padding: '4px 8px',
                    borderRadius: '4px',
                    backgroundColor:
                      currentPage === index ? '#67c7aa' : 'white',
                    color: currentPage === index ? 'white' : 'black',
                    cursor: 'pointer',
                  }}
                >
                  {index + 1}
                </div>
              )
            })}
            <div
              onClick={() => table.nextPage()}
              style={{
                padding: '4px 8px',
                borderRadius: '4px',
                color: !table.getCanNextPage() ? '#ccc' : '#666',
                cursor: !table.getCanNextPage() ? 'not-allowed' : 'pointer',
              }}
            >
              <i className="fa-solid fa-angle-right" />
            </div>
          </div>
        </div>
      </Container>
    </>
  )
}

export default PatientData
